import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import { TOP_NAV, NAV_GROUPS, slug, isActivePath } from "./navConfig";

const OPEN_KEY = "otm_admin_nav_open";
const TONES = {
  green: ["bg-[#73bf44] text-[#050505]", "bg-[#050505] text-[#73bf44]"],
  amber: ["bg-amber-500 text-[#050505]", "bg-[#050505] text-amber-400"],
  red: ["bg-red-500 text-white", "bg-[#050505] text-red-400"],
};

function CountBadge({ badge, active, testId }) {
  if (!badge?.count) return null;
  return (
    <span data-testid={testId}
      className={`ml-auto text-[0.65rem] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center ${TONES[badge.tone][active ? 1 : 0]}`}>
      {badge.count}
    </span>
  );
}

function NavItem({ item, badge, nested }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end} data-testid={`admin-nav-${slug(item)}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 ${nested ? "py-2" : "py-2.5"} text-sm transition-colors ${
        isActive ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white hover:bg-white/[0.04]"}`}>
      {({ isActive }) => (
        <>
          <Icon size={nested ? 16 : 18} weight={isActive ? "fill" : "regular"} className="shrink-0" />
          <span className="truncate">{item.label}</span>
          <CountBadge badge={badge} active={isActive} testId={badge?.testId} />
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ group, open, onToggle, active, badges }) {
  const Icon = group.icon;
  const total = group.items.reduce((n, i) => n + (badges[i.to]?.count || 0), 0);
  const tone = group.items.map((i) => badges[i.to]).find((b) => b?.count)?.tone;
  return (
    <div data-testid={`admin-nav-group-${group.key}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`admin-nav-group-${group.key}-toggle`}
        className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-white/[0.04] ${active ? "text-white font-semibold" : "text-neutral-300 hover:text-white"}`}>
        <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-[#73bf44] shrink-0" : "shrink-0"} />
        <span className="truncate">{group.label}</span>
        {!open && <CountBadge badge={{ count: total, tone }} testId={`admin-nav-group-${group.key}-badge`} />}
        <CaretDown size={13} className={`shrink-0 text-neutral-500 transition-transform duration-200 ${open ? "rotate-180" : ""} ${!open && total ? "" : "ml-auto"}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="ml-[1.35rem] pl-2.5 border-l border-neutral-800 mt-0.5 mb-1.5 space-y-0.5" data-testid={`admin-nav-group-${group.key}-items`}>
            {group.items.map((item) => <NavItem key={item.to} item={item} badge={badges[item.to]} nested />)}
          </div>
        </div>
      </div>
    </div>
  );
}

const readOpen = () => {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY)) || []; } catch { return []; }
};

export function AdminNav({ badges }) {
  const { pathname } = useLocation();
  const activeGroup = NAV_GROUPS.find((g) => g.items.some((i) => isActivePath(pathname, i)))?.key;
  const [open, setOpen] = React.useState(readOpen);

  React.useEffect(() => {
    if (activeGroup) setOpen((o) => (o.includes(activeGroup) ? o : [...o, activeGroup]));
  }, [activeGroup]);
  React.useEffect(() => { localStorage.setItem(OPEN_KEY, JSON.stringify(open)); }, [open]);

  const toggle = (key) => setOpen((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3" data-testid="admin-nav">
      <div className="space-y-1">
        {TOP_NAV.map((item) => <NavItem key={item.to} item={item} badge={badges[item.to]} />)}
      </div>
      <div className="my-3 mx-3 border-t border-neutral-800/80" />
      <div className="space-y-0.5">
        {NAV_GROUPS.map((g) => (
          <NavGroup key={g.key} group={g} open={open.includes(g.key)} onToggle={() => toggle(g.key)} active={g.key === activeGroup} badges={badges} />
        ))}
      </div>
    </nav>
  );
}
